import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PaymentLinkStats from "../components/PaymentLinkStats";
import PaymentLinkTable from "../components/PaymentLinkTable";
import AppHeading1 from "components/ui/AppHeading1.jsx";
import { UTILS } from "utils";
import { API } from "api";
import { errorHandler } from "api/api/errorHandler";
import { downloadReports } from "utils/Utils";
import { errorNotification } from "utils/Utils";
import AppContainer from "components/ui/AppContainer.jsx";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton.jsx";
import AppDatePicker from "components/ui/AppDatePicker.jsx";
import moment from "moment";

const PaymentLinks = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [earning, setEarning] = useState(0);
  const [paymentLinkData, setPaymentData] = useState({
    data: [],
    totalPages: 1,
  });

  const resetstate = () => {
    setPaymentData({
      data: [],
      totalPages: 1,
    });
    setEarning(0);
  };

  const toApiDate = (iso) => {
    if (!iso) return "";
    const m = moment(iso, "YYYY-MM-DD", true);
    return m.isValid() ? m.format("DD/MM/YYYY") : "";
  };

  const getPaymentLinksHistory = async (pageNumber) => {
    try {
      const formattedStartDate = toApiDate(startDate);
      const formattedEndDate = toApiDate(endDate);
      if (
        !UTILS.validateSelectedDates(formattedStartDate, formattedEndDate)
      ) {
        return;
      }
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.GET_PAYMENTLINK_HISTORY({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        brandId: userDetail.brandId,
        page: pageNumber ?? 1,
      });
      if (result && result.success === 1 && result?.data?.fetchLinks) {
        if (result?.data?.fetchLinks?.length > 0) {
          setPaymentData({
            data: result?.data?.fetchLinks,
            totalPages: result.data.count,
          });

          setEarning(UTILS.totalLinksEarning(result?.data?.fetchLinks));
          if (result?.message) {
            UTILS.handleSuccesNotification(t(result.message));
          }
        } else {
          resetstate();
          UTILS.handleSuccesNotification(t("No Payment Links Found"));
        }
      } else {
        resetstate();
        UTILS.handleErrorNotification(
          t(result?.error?.message || errorHandler(result?.error))
        );
      }
    } catch (error) {
      resetstate();
      UTILS.handleErrorNotification(
        t(error?.error?.message || errorHandler(error?.error))
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadexcel = async () => {
    try {
      const formattedStartDate = toApiDate(startDate);
      const formattedEndDate = toApiDate(endDate);
      if (
        !UTILS.validateSelectedDates(formattedStartDate, formattedEndDate)
      ) {
        return;
      }
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.downloadPaymentLinksExcelReport({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        brandId: userDetail.brandId,
      });
      if (result?.success === 1 && result?.data) {
        UTILS.handleSuccesNotification(
          t("Payment Links Exported Successfully")
        );
        const url = result?.data;
        downloadReports(url);
      } else {
        UTILS.handleErrorNotification(
          t(result?.error?.message || errorHandler(result?.error))
        );
      }
    } catch (error) {
      errorNotification(errorHandler(error?.error?.message || error?.error));
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date && date.isValid() ? date.format("YYYY-MM-DD") : null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date && date.isValid() ? date.format("YYYY-MM-DD") : null);
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    if (!toApiDate(startDate) || !toApiDate(endDate)) return;
    getPaymentLinksHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when range changes; handler reads latest dates.
  }, [startDate, endDate]);

  return (
    <AppContainer
      title={t("Payment Links History")}
      subTitle={t(
        "Here you can track all your past orders, earnings, and payment details in one place."
      )}
      loading={loading}
    >
      <div className="flex w-full flex-col gap-3 py-4 md:flex-row md:flex-wrap md:items-end">
        <div className="w-full min-w-0 md:min-w-[18rem] md:w-80 lg:w-96 md:flex-shrink-0">
          <AppDatePicker
            label={t("Start date")}
            value={startDate || undefined}
            onChange={handleStartDateChange}
          />
        </div>
        <div className="w-full min-w-0 md:min-w-[18rem] md:w-80 lg:w-96 md:flex-shrink-0">
          <AppDatePicker
            label={t("End date")}
            value={endDate || undefined}
            onChange={handleEndDateChange}
          />
        </div>
      </div>

      <PaymentLinkStats earning={earning} />
      <div className="my-3 flex min-w-0 flex-wrap items-center justify-between gap-4">
        <AppHeading1>{t("Links")}</AppHeading1>
        <div className="flex items-center justify-end gap-3 sm:w-auto md:max-w-2xl">
          <AppSearchInput
            value={searchTerm}
            placeholder={t("Search by store, link id, creator, or status")}
            onInput={(e) => setSearchTerm(e.target.value)}
          />
          {paymentLinkData?.data?.length > 0 && (
            <AppButton onClick={downloadexcel} type="primary">
              {t("Export")}
            </AppButton>
          )}
        </div>
      </div>
      <PaymentLinkTable
        paymentLinks={paymentLinkData}
        searchTerm={searchTerm}
      />
    </AppContainer>
  );
};

export default PaymentLinks;
